import * as z from 'zod'
import { TFunction } from 'i18next'
import { getDocumentCustomerIssues, getDocumentCustomerSchema } from 'src/app/components/document-customer-input/document-customer-schema'
import { CheckoutDocumentGroupType, FiscalType } from 'src/api/api.generated'
import { todayStartOfTheDay } from 'src/utils/date'

export function getDocumentDetailsSchema() {
  return z
    .object({
      amount: z.number(),
      date: z.date(),
      description: z.string(),
      quantity: z.number(),
      reference: z.object({
        accountantCategoryId: z.string().optional(),
        chargeCategoryId: z.string().optional(),
      }),
      referenceDocumentId: z.string().optional(),
      vatId: z.string().optional(),
      billId: z.string().optional(),
      productionAssignedIs: z.string().optional(),
    })
    .array()
}

const documentDetailsSchema = getDocumentDetailsSchema()
export type DocumentDetailsSchema = z.infer<typeof documentDetailsSchema>

export function getCheckoutDocumentFormSchema(t?: TFunction, unpaidPaymentActiveIds?: string[]) {
  return z
    .object({
      amounts: getCheckoutAmountsSchema(),
      fiscalType: z.nativeEnum(FiscalType),
      cashDeskId: z.string().min(1),
      customer: getDocumentCustomerSchema(),
      payments: getPaymentSchema(t).array(),
      deposits: getCheckoutDepositsSchema(t),
      splitPayment: z.boolean().default(false),
      details: getDocumentDetailsSchema().optional(),
      scelNote: z.string().optional(),
      note: z.string().optional(),
      groupBy: z.nativeEnum(CheckoutDocumentGroupType).optional(),
      addNoteToZeroReceipt: z.boolean().optional(),
      companyToAddZeroReceipt: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const customerIssues = getDocumentCustomerIssues('customer', data.customer, data.fiscalType, t)
      customerIssues.forEach((i) => ctx.addIssue(i))

      const paymentSum = data.payments.reduce((prev, current) => {
        return prev + (current.amount || 0)
      }, 0)

      const paymentAmount = data.amounts.paymentAmount

      if (paymentAmount && paymentAmount >= 0 && paymentSum !== paymentAmount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.create_deposit.payments_error'),
          path: ['payments'],
        })
      }

      const hasUnpaidPaymentActive = data.payments.some((p) => p.paymentActiveId && unpaidPaymentActiveIds?.includes(p.paymentActiveId))

      if (hasUnpaidPaymentActive) {
        const isValid = data.payments
          .filter((p) => p.paymentActiveId)
          .every((p) => {
            // Typescript anomaly p.paymentActiveId can't be undefined because it was checked with filter
            if (unpaidPaymentActiveIds?.includes(p.paymentActiveId!) && (p.expireDate === undefined || p.expireDate === null)) return false
            return true
          })

        if (!isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('pms.accounting.documents.missing_unpaid_expiration_date'),
            path: ['payments'],
          })
        }
      }

      if (data.addNoteToZeroReceipt && !data.companyToAddZeroReceipt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t ? t('pms.accounting.documents.missing_company_to_add_zero_receipt') : 'Missing company to add zero receipt',
          path: ['companyToAddZeroReceipt'],
        })
      }

      return true
    })
}

export function getCheckoutAmountsSchema() {
  return z.object({
    billsAmount: z.number().gte(0),
    fiscalDepositsAmount: z.number().min(0),
    totalAmount: z.number().min(0),
    notFiscalDepositsAmount: z.number().min(0),
    paymentAmount: z.number().min(0),
  })
}
const checkoutAmountsSchema = getCheckoutAmountsSchema()
export type CheckoutAmountsSchema = z.infer<typeof checkoutAmountsSchema>

export function getCheckoutDepositsSchema(t?: TFunction) {
  return z
    .object({
      uuid: z.string().uuid(),
      depositAssignedId: z.string().min(1),
      fiscalType: z.nativeEnum(FiscalType),
      maxAmount: z.number(),
      usedAmount: z.number().nullable(),
      vatPercentage: z.number(),
    })
    .superRefine((data, ctx) => {
      if (data.usedAmount !== null && data.usedAmount !== undefined && data.usedAmount > data.maxAmount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.checkout_document.error.deposit_amount_gt_residual'),
          path: ['amount'],
        })
      }
    })
    .array()
    .optional()
}
const checkoutDepositsSchema = getCheckoutDepositsSchema()
export type CheckoutDepositsSchema = z.infer<typeof checkoutDepositsSchema>

const checkoutDocumentFormSchema = getCheckoutDocumentFormSchema()
export type CheckoutDocumentFormSchema = z.infer<typeof checkoutDocumentFormSchema>

export function getPaymentSchema(t?: TFunction) {
  const today = todayStartOfTheDay()

  return z.object({
    uuid: z.string().uuid(),
    paymentActiveId: z.string().min(1).optional(),
    amount: z.number().min(0),
    editable: z.boolean().default(true),
    expireDate: z
      .date()
      .optional()
      .nullable()
      .refine((date) => (date ? date >= today : true), { message: t?.('validation.expire_date_gte_today') }),
  })
}

const paymentSchema = getPaymentSchema()
export type PaymentSchema = z.infer<typeof paymentSchema>
